import {
  Placement,
  ReferenceElement,
  Strategy,
  Middleware,
  autoUpdate,
  offset,
  computePosition,
  FloatingElement,
  flip,
  arrow,
  shift,
} from "@floating-ui/dom";

import type {
  ReactiveController,
  ReactiveControllerHost,
} from "@lit/reactive-element";
import type { ThemeInjector } from "../theme-helper/ThemeInjector";

export interface FloatableElement {
  floating?: FloatSettings;
}

export type FloatableHost = ReactiveControllerHost &
  FloatingElement &
  FloatableElement;

export class FloatingUiController implements ReactiveController {
  host: FloatableHost;

  private cleanupAutoUpdate?: () => void;
  private _settings?: FloatSettings;
  private _reference?: ReferenceElement;
  private _middleware: Middleware[] = [];
  private _resizeObserver?: ResizeObserver;
  private _floating: boolean = false;
  arrowEl?: HTMLElement;

  constructor(host: FloatableHost) {
    (this.host = host).addController(this);
  }

  hostConnected(): void {
    if (this.host.floating) {
      this.float(this.host.floating);
    }
  }

  hostDisconnected() {
    this.destroy();
  }

  hostUpdated() {
    if (this.host.floating && !this._floating) {
      this.float(this.host.floating);
    }
    if (this._floating) {
      this.updatePosition();
    }
  }

  hostResized() {
    if (this._floating) {
      this.updatePosition();
    }
  }

  float(settings: FloatSettings) {
    if (this._floating) {
      return;
    }

    this._floating = true;
    this._settings = settings;
    this._reference = this.getReference();
    this._resizeObserver = new ResizeObserver(() => this.hostResized());

    if (!this._settings) {
      throw new Error("Floating settings are required");
    }

    if (!this._reference) {
      throw new Error("Floating reference is required");
    }

    this.host.classList.add("wk-floating");

    const {
      position,
      arrow: arrowSetting = false,
      flip: flipSetting = false,
      shift: shiftSetting = false,
      animationFrame = false,
    } = this._settings;

    const offsetSetting = this.getOffsetSetting();

    Object.assign(this.host.style, {
      position: position.strategy ?? "absolute",
      top: "0",
      left: "0",
      transition: "none",
      transform: "translate3d(0,0,0)",
    });

    if (position.inset && !arrowSetting) {
      this._middleware.push(
        offset(({ rects, placement }) => {
          if (placement.includes("top") || placement.includes("bottom")) {
            return {
              mainAxis: -rects.floating.height - (offsetSetting?.mainAxis ?? 0),
              crossAxis: offsetSetting?.crossAxis ?? 0,
              alignmentAxis: offsetSetting?.alignmentAxis ?? 0,
            };
          } else {
            return {
              mainAxis: -rects.floating.width - (offsetSetting?.mainAxis ?? 0),
              crossAxis: offsetSetting?.crossAxis ?? 0,
              alignmentAxis: offsetSetting?.alignmentAxis ?? 0,
            };
          }
        })
      );
    } else if (offsetSetting) {
      this._middleware.push(offset(offsetSetting));
    }

    if (flipSetting) {
      this._middleware.push(flip());
    }

    if (shiftSetting) {
      this._middleware.push(shift());
    }

    if (arrowSetting) {
      this.arrowEl = document.createElement("div");
      this.arrowEl.classList.add("wk-arrow");

      this._middleware.push(arrow({ element: this.arrowEl }));
    }

    this.cleanupAutoUpdate = autoUpdate(
      this._reference,
      this.host,
      () => {
        this.updatePosition();
      },
      {
        animationFrame,
      }
    );

    this._resizeObserver.observe(this.host);
  }

  getOffsetSetting() {
    const offset = this._settings?.offset;

    if (!offset || typeof offset === "boolean") {
      return undefined;
    }

    if (typeof offset === "number") {
      return {
        mainAxis: offset,
        alignmentAxis: this._settings?.position.inset ? offset : null,
      };
    }

    return offset;
  }

  destroy() {
    if (this._floating) {
      Object.assign(this.host.style, {
        position: "",
        top: "",
        left: "",
        transition: "",
        transform: "",
      });

      this.arrowEl?.parentElement?.removeChild(this.arrowEl);
      this.arrowEl = undefined;

      this._middleware = [];

      this.host.classList.remove("wk-floating");
      this.cleanupAutoUpdate?.();
      this._resizeObserver?.disconnect();
      this._floating = false;
    }
  }

  updatePosition() {
    if (!this._reference || !this._settings) {
      return;
    }

    const { strategy = "absolute", placement = "top" } =
      this._settings.position ?? {};

    computePosition(this._reference, this.host, {
      middleware: this._middleware,
      strategy,
      placement,
    }).then(({ x, y, placement, middlewareData }) => {
      if (middlewareData.arrow && this.arrowEl) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow;

        const staticSide = {
          top: "bottom",
          right: "left",
          bottom: "top",
          left: "right",
        }[placement.split("-")[0]];

        const arrowRotation = {
          bottom: "0deg",
          left: "90deg",
          top: "180deg",
          right: "270deg",
        }[placement.split("-")[0]];

        Object.assign(this.arrowEl.style, {
          left: arrowX != null ? `${arrowX}px` : "",
          top: arrowY != null ? `${arrowY}px` : "",
          right: "",
          bottom: "",
          [staticSide!]: "-5px",
          transform: `rotate(${arrowRotation})`,
        });
      }

      Object.assign(this.host.style, {
        transform: `translate3d(${Math.round(x)}px,${Math.round(y)}px,0)`,
      });
    });
  }

  getReference() {
    if (!this._settings) {
      return;
    }

    const reference = this._settings.reference;

    if (typeof reference === "string") {
      if (reference === "viewport") {
        return this.createViewportReference();
      }

      const queryElement = document.querySelector(reference);
      if (!queryElement) {
        throw new Error(`Floating reference element (${reference}) not found`);
      }
      return queryElement;
    }

    if (reference.hasOwnProperty("element")) {
      return (reference as ThemeInjector).element;
    }

    return reference as ReferenceElement;
  }

  createViewportReference() {
    return {
      getBoundingClientRect() {
        return {
          x: 0,
          y: 0,
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          width: window.visualViewport?.width ?? 0,
          height: window.visualViewport?.height ?? 0,
        };
      },
    };
  }
}

export interface FloatSettings {
  reference: FloatingReference;
  position: FloatingPosition;
  flip?: boolean;
  shift?: boolean;
  arrow?: boolean;
  offset?: boolean | OffsetValue;
  animationFrame?: boolean;
}

export type FloatingReference = ReferenceElement | ThemeInjector | string;
export interface FloatingPosition {
  placement?: FloatingPlacement;
  strategy?: FloatingStrategy;
  inset?: boolean;
}

export type FloatingPlacement = Placement;
export type FloatingStrategy = Strategy;

export type OffsetValue =
  | number
  | {
      mainAxis?: number;
      crossAxis?: number;
      alignmentAxis?: number | null;
    };
